import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
// Import the report overview components
import JiSiReportsOverview from "../components/JiSiReportsOverview";
import SiFcReportsOverview from "../components/SiFcReportsOverview";
import ClassroomObservationsOverview from "../components/ClassroomObservationsOverview";
import {
  whoami,
  getTeachers,
  getClassesBySchoolId,
  getClassesByTeacherId,
  getClassAssessmentsForMultipleClasses,
  getStudentsForClass,
  getStudents,
  getResultsByStudent,
  getScreenerAssessmentIds,
  getCountryId,
  getCountryGrades,
} from "../services/APIClient";
import { formatAcademicYear, isDateInAcademicYear } from "../utils/academicYear";
import useAcademicYearOptions from "../utils/useAcademicYearOptions";
import useReportingConfiguration from "../hooks/useReportingConfiguration";
import { TEACHER_DASHBOARD_NAVIGATION_IDS } from "../config/teacherDashboardNavigation";
import "../components/ClassOverview.css";

const getRecordAcademicYear = (record) =>
  record?.academicYear || record?.academic_year || record?.year;

export const isAssessmentInAcademicYear = (assessment, academicYear) => {
  const storedYear = getRecordAcademicYear(assessment);
  if (storedYear) {
    try {
      return formatAcademicYear(storedYear, "storage") === academicYear.storage;
    } catch (error) {
      return false;
    }
  }

  const assessmentDate =
    assessment?.date ||
    assessment?.assessmentDate ||
    assessment?.dateCreated ||
    assessment?.createdAt;

  if (!assessmentDate) return false;

  try {
    return isDateInAcademicYear(assessmentDate, academicYear);
  } catch (error) {
    return false;
  }
};

// Grade level key map mirrors the pattern in SencoStudentResultsOverview
const GRADE_LEVEL_KEY_MAP = { '1': 'junior', '2': 'senior', '3': 'first' };
// "jisi" shows junior assessments; "sifc" shows senior assessments
const REPORT_TYPE_GRADE_KEY = { jisi: 'junior', sifc: 'senior', grade3: 'first' };

export const filterAssessmentsForAcademicYear = (assessments, academicYear) =>
  (assessments || []).filter((assessment) =>
    isAssessmentInAcademicYear(assessment, academicYear)
  );

function HandoverDocs({ asOfDate }) {
  const reportingConfiguration = useReportingConfiguration();
  const pageCopy =
    reportingConfiguration[TEACHER_DASHBOARD_NAVIGATION_IDS.CLASSROOM_HANDOVER];
  // State for user data
  const [schoolid, setSchoolid] = useState(null);
  const [teacherid, setTeacherid] = useState(null);
  const [gradeNames, setGradeNames] = useState({
    grade1: "Grade 1",
    grade2: "Grade 2",
    grade3: "Grade 3",
  });
  const [name, setName] = useState("");
  const [additionalRoles, setAdditionalRoles] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [gradeLevel, setGradeLevel] = useState("");
  const [selectedReportType, setSelectedReportType] = useState(null); // Track which report button was pressed
  
  // State for class data
  const [classes, setClasses] = useState([]);
  const [classIds, setClassIds] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [allClassesAssessments, setAllClassesAssessments] = useState([]);
  
  // UI state
  const [selectedYear, setSelectedYear] = useState(null);
  const academicYearOptions = useAcademicYearOptions({ asOfDate, previousYears: 1 });
  const selectedYearOption = academicYearOptions.find(
    ({ display }) => display === selectedYear
  );
  const previousCurrentYear = useRef(academicYearOptions[0]?.display);
  const [isLoading, setIsLoading] = useState(true);
  const [showReports, setShowReports] = useState(false);
  const [error, setError] = useState(null);
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)")?.matches
  );

  // State for student data
  const [studentsData, setStudentsData] = useState({});
  const [assessmentResultsByStudent, setAssessmentResultsByStudent] = useState({});
  const [studentsReadingScores, setStudentsReadingScores] = useState({});
  
  // Loading states for assessment data
  const [assessmentDataLoading, setAssessmentDataLoading] = useState({});
  const [readingScoresCalculating, setReadingScoresCalculating] = useState({});
  const [batchProgress, setBatchProgress] = useState({});
  const [assessmentIdsByGrade, setAssessmentIdsByGrade] = useState(null);

  const emptyAssessmentScores = { baseline: null, midpoint: null, endOfYear: null };

  useEffect(() => {
    if (!selectedYear && academicYearOptions[0]?.display) {
      setSelectedYear(academicYearOptions[0].display);
    }
  }, [academicYearOptions, selectedYear]);

  useEffect(() => {
    const currentYear = academicYearOptions[0]?.display;
    if (selectedYear === previousCurrentYear.current && currentYear) {
      setSelectedYear(currentYear);
    }
    previousCurrentYear.current = currentYear;
  }, [academicYearOptions, selectedYear]);

  const getAssessmentType = (name = "") => {
    const n = name.toLowerCase();
    if (/baseline|autumn/.test(n)) return "baseline";
    if (/midpoint|mid\s*point|spring/.test(n)) return "midpoint";
    if (/end\s*of\s*year|summer|eoy/.test(n)) return "endOfYear";
    return null;
  };

  // Functions for Overall Standard Score vs Test calculation
  const groupByStudentId = (results) => {
    return results.reduce((acc, result) => {
      const studentId = result.student.id;
      const studentName = result.student.name;
      if (!acc[studentId]) {
        acc[studentId] = { name: studentName, assessments: [] };
      }
      acc[studentId].assessments.push(result);
      return acc;
    }, {});
  };

  const calculateReadingScores = (groupedResults) => {
    const scores = [];
    Object.keys(groupedResults).forEach(studentId => {
      const { name, assessments } = groupedResults[studentId];
      let totalScore = 0;
      let taskCount = 0;
      
      assessments.forEach(assessment => {
        assessment.tasks.forEach(task => {
          if (task.score !== undefined) {
            totalScore += task.score;
            taskCount++;
          }
        });
      });
      
      scores.push({
        studentName: name,
        studentId: studentId,
        readingScore: taskCount > 0 
          ? Math.round((totalScore / taskCount) * 100) 
          : "No completed tasks"
      });
    });
    return scores;
  };

  const calculateAssessmentScoresByStudent = (students, results) => {
    const resultsByStudent = results.reduce((acc, result) => {
      const studentId = result.student?.id;
      if (!studentId) return acc;
      if (!acc[studentId]) acc[studentId] = [];
      acc[studentId].push(result);
      return acc;
    }, {});

    return students.reduce((acc, student) => {
      // Build a map from assessment instance ID → type (baseline/midpoint/endOfYear)
      // using the assessment name as the source of truth, not position
      const instanceIdToType = {};

      filterAssessmentsForAcademicYear(student.assessments, selectedYearOption)
        .forEach((assessment) => {
        const type = getAssessmentType(assessment.name);
        if (type) {
          instanceIdToType[assessment.id] = type;
        }
      });

      const assessmentScores = { ...emptyAssessmentScores };

      const studentResults = resultsByStudent[student.id] || [];

      studentResults.forEach((result) => {
        // Match by assessmentInstance ID — v2 endpoint uses this field, not result.id
        const matchedType = instanceIdToType[result.assessmentInstance];

        if (!matchedType || !Array.isArray(result.tasks)) return;

        const validTasks = result.tasks.filter((task) => task.score !== undefined);
        assessmentScores[matchedType] =
          validTasks.length > 0
            ? Math.round((validTasks.reduce((sum, task) => sum + task.score, 0) / validTasks.length) * 100)
            : "No completed tasks";
      });

      acc[student.id] = assessmentScores;
      return acc;
    }, {});
  };

  const mergeStudentsWithScores = (students, scores, assessmentScoresByStudent = {}) => {
    const scoresMap = scores.reduce((map, score) => {
      map[score.studentId] = score.readingScore;
      return map;
    }, {});
    
    return students.map(student => ({
      ...student,
      readingScore: scoresMap[student.id] ?? null,
      assessmentScores: assessmentScoresByStudent[student.id] || { ...emptyAssessmentScores }
    }));
  };

  // Handover report functions
  const jisiReports = () => {
    console.log("Junior-Senior handover selected");
    setGradeLevel("grade_level1");
    setSelectedReportType("jisi");
    setSelectedClass(null);
    setStudentsData({});
    setAssessmentResultsByStudent({});
    setStudentsReadingScores({});
  };

  const sifcReports = () => {
    console.log("Senior-First Class handover selected");
    setGradeLevel("grade_level2");
    setSelectedReportType("sifc");
    setSelectedClass(null);
    setStudentsData({});
    setAssessmentResultsByStudent({});
    setStudentsReadingScores({});
  };

  const grade3Reports = () => {
    setGradeLevel("grade_level3");
    setSelectedReportType("grade3");
    setSelectedClass(null);
    setStudentsData({});
    setAssessmentResultsByStudent({});
    setStudentsReadingScores({});
  };

  // Initialize user data on component mount
  useEffect(() => {
    const initialize = async () => {
      try {
        const whoamiResponse = await whoami();
        console.log("Whoami response:", whoamiResponse);
        const { schoolid, id, name, additionalRoles } = whoamiResponse.data.stakeholder;

        setSchoolid(schoolid);
        setTeacherid(id);
        setName(name);
        setAdditionalRoles(additionalRoles);
        
        // Get teachers for the school
        const teachersResponse = await getTeachers(schoolid);
        setTeachers(Array.isArray(teachersResponse) ? teachersResponse : []);
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error initializing:", error);
        setError("Failed to load user data. Please refresh the page.");
        setIsLoading(false);
      }
    };

    initialize();
  }, []);

  // Fetch region-specific grade names
  useEffect(() => {
    if (!schoolid) return;
    const fetchGradeData = async () => {
      try {
        const countryResponse = await getCountryId(schoolid);
        if (countryResponse.countryId) {
          const gradesResponse = await getCountryGrades(countryResponse.countryId);
          const { grade1, grade2, grade3 } = gradesResponse;
          setGradeNames({ grade1, grade2, grade3 });
        }
      } catch (error) {
        console.error("Error fetching grade data:", error);
      }
    };
    fetchGradeData();
  }, [schoolid]);

  // Fetch screener assessment IDs dynamically (replaces hardcoded ASSESSMENT_TEMPLATE_IDS)
  useEffect(() => {
    if (!schoolid) return;
    getScreenerAssessmentIds(schoolid)
      .then((data) => {
        const mapping = {};
        Object.entries(data).forEach(([level, points]) => {
          const key = GRADE_LEVEL_KEY_MAP[level];
          if (key) mapping[key] = points;
        });
        setAssessmentIdsByGrade(mapping);
      })
      .catch((error) => {
        console.error('Failed to fetch screener assessment IDs:', error);
        setAssessmentIdsByGrade({});
      });
  }, [schoolid]);

  // Fetch classes when a year is selected
  useEffect(() => {
    if (!teacherid || !selectedYear) return;

    setIsLoading(true);
    setError(null);
    
    // Determine which API to use based on teacher roles
    const roleString = typeof additionalRoles === 'string' ? additionalRoles : 
                      Array.isArray(additionalRoles) ? additionalRoles.join(' ') : 
                      additionalRoles ? String(additionalRoles) : '';
    
    const isSencoOrSet = roleString && 
      (roleString.toLowerCase().includes('senco') || 
       roleString.toLowerCase().includes('set'));

    const classesPromise = isSencoOrSet 
      ? getClassesBySchoolId(schoolid)    // SENCO/SET sees all school classes
      : getClassesByTeacherId(teacherid); // Regular teachers see only their classes

    classesPromise
      .then((response) => {
        console.log("Classes response:", response);
        console.log(`Using ${isSencoOrSet ? 'school-based' : 'teacher-based'} class retrieval`);
        
        setClasses(response || []);
        
        if (!response || response.length === 0) {
          console.log("No classes found for this teacher/school and year range");
          setIsLoading(false);
          setShowReports(true);
          return;
        }
        
        // Don't auto-select any class - let user choose
        setSelectedClass(null);
        
        const ids = response?.map((classItem) => classItem.id);
        setClassIds(ids);
        
        setIsLoading(false);
        setShowReports(true);
      })
      .catch((err) => {
        console.error(`Error fetching classes by ${isSencoOrSet ? 'school' : 'teacher'} ID:`, err);
        setError("Failed to load classes. Please try again.");
        setIsLoading(false);
      });
  }, [schoolid, teacherid, selectedYear, additionalRoles]);

  // Actively fetch students when an accordion is selected
  const fetchStudentsForSelectedClass = (classId) => {
    console.log("FETCHING STUDENTS for class:", classId);
    
    // Clear students data for this class to ensure fresh data
    setStudentsData(prev => ({
      ...prev,
      [classId]: null  // Set to null to indicate loading
    }));
    
    // Make the API call
    getStudents(classId)
      .then(response => {
        console.log("===== STUDENT DATA RECEIVED =====");
        console.log("Class ID:", classId);
        console.log("Raw response:", response);
        
        // Verify the response is what we expect
        if (response && (Array.isArray(response) || response.students)) {
          // Handle both array response and object with students property
          const studentsList = Array.isArray(response) ? response : response.students;
          
          console.log("Students list type:", typeof studentsList);
          console.log("Is array?", Array.isArray(studentsList));
          console.log("Length:", studentsList?.length || 0);
          
          if (studentsList && studentsList.length > 0) {
            console.log("First student sample:", studentsList[0]);
            
            // Store the students in state
            setStudentsData(prev => ({
              ...prev,
              [classId]: studentsList
            }));
            
            // Clear any previous errors
            setError(null);
          } else {
            console.log("No students found in the response");
            setStudentsData(prev => ({
              ...prev,
              [classId]: []  // Empty array if no students
            }));
          }
        } else {
          console.error("Unexpected response format:", response);
          setStudentsData(prev => ({
            ...prev,
            [classId]: []  // Empty array on error
          }));
        }
      })
      .catch(error => {
        console.error("Error fetching students for class " + classId + ":", error);
        setError("Failed to load student data. Please try again.");
        setStudentsData(prev => ({
          ...prev,
          [classId]: []  // Empty array on error
        }));
      });
  };

  // Fetch assessment results for students
  useEffect(() => {
    const fetchAssessmentResults = (classId, students) => {
      if (!students || students.length === 0) return;
      
      // Determine which assessment template IDs to include based on report type
      const gradeKey = REPORT_TYPE_GRADE_KEY[selectedReportType];
      const allowedTemplateIds = Object.values(assessmentIdsByGrade?.[gradeKey] || {}).filter(Boolean);

      // Extract assessment instance IDs from students, filtered by assessmentid
      const assessmentInstanceIds = students.reduce((acc, student) => {
        const ids = student.assessments
          ? student.assessments
              .filter(assessment =>
                allowedTemplateIds.includes(assessment.assessmentid) &&
                isAssessmentInAcademicYear(assessment, selectedYearOption)
              )
              .map(assessment => assessment.id)
          : [];
        return [...acc, ...ids];
      }, []);
      
      if (assessmentInstanceIds.length === 0) {
        console.log(`No matching assessment instances found for class ${classId} and report type ${selectedReportType}`);
        
        // Even if no assessments, we should still populate studentsReadingScores
        // This ensures all students appear in the table
        const studentsWithNoScores = students.map(student => ({
          ...student,
          readingScore: "No assessments",
          assessmentScores: { ...emptyAssessmentScores }
        }));
        
        setStudentsReadingScores(prev => ({
          ...prev,
          [classId]: studentsWithNoScores
        }));
        
        return;
      }
      
      console.log(`Fetching assessment results for class ${classId} with ${assessmentInstanceIds.length} instances (filtered for ${selectedReportType})`);
      
      // Set loading state for this class
      setAssessmentDataLoading(prev => ({
        ...prev,
        [classId]: true
      }));
      
      // Initialize batch progress if there are many assessment instances
      if (assessmentInstanceIds.length > 30) {
        setBatchProgress(prev => ({
          ...prev,
          [classId]: { current: 0, total: Math.ceil(assessmentInstanceIds.length / 30) }
        }));
      }
      
      // Fetch results by student
      getResultsByStudent(assessmentInstanceIds)
        .then(response => {
          console.log(`Received assessment results for class ${classId}:`, response);
          setAssessmentResultsByStudent(prev => ({
            ...prev,
            [classId]: response
          }));
          
          // Clear loading state and batch progress for this class
          setAssessmentDataLoading(prev => ({
            ...prev,
            [classId]: false
          }));
          setBatchProgress(prev => ({
            ...prev,
            [classId]: null
          }));
        })
        .catch(error => {
          console.error(`Error fetching assessment results for class ${classId}:`, error);
          
          // Clear loading state and batch progress on error
          setAssessmentDataLoading(prev => ({
            ...prev,
            [classId]: false
          }));
          setBatchProgress(prev => ({
            ...prev,
            [classId]: null
          }));
        });
    };
    
    // Wait until screener assessment IDs have been fetched before running
    if (!assessmentIdsByGrade) return;

    // Check if we have loaded students for any class
    Object.keys(studentsData).forEach(classId => {
      if (studentsData[classId] && studentsData[classId].length > 0) {
        fetchAssessmentResults(classId, studentsData[classId]);
      }
    });
  }, [studentsData, selectedReportType, assessmentIdsByGrade, selectedYearOption]);

  // Calculate Overall Standard Score vs Test when assessment results change
  useEffect(() => {
    const updateReadingScores = () => {
      const updatedScores = {};
      
      Object.keys(studentsData).forEach(classId => {
        const students = studentsData[classId];
        
        if (students && students.length > 0) {
          // Always start with all students
          let mergedStudents = students.map(student => ({
            ...student,
            readingScore: null // Default to null for students without scores
          }));
          
          // If we have assessment results for this class, calculate scores
          const results = assessmentResultsByStudent[classId];
          if (results && results.length > 0) {
            console.log(`Calculating Overall Standard Score vs Test for class ${classId}`);
            
            // Set calculating state for this class
            setReadingScoresCalculating(prev => ({
              ...prev,
              [classId]: true
            }));
            
            // Use setTimeout to allow UI to update before heavy calculation
            setTimeout(() => {
              const groupedResults = groupByStudentId(results);
              const readingScores = calculateReadingScores(groupedResults);
              const assessmentScoresByStudent = calculateAssessmentScoresByStudent(students, results);
              
              // Merge scores with all students (including those without assessments)
              mergedStudents = mergeStudentsWithScores(students, readingScores, assessmentScoresByStudent);
              
              // Update scores and clear calculating state
              setStudentsReadingScores(prev => ({
                ...prev,
                [classId]: mergedStudents
              }));
              
              setReadingScoresCalculating(prev => ({
                ...prev,
                [classId]: false
              }));
            }, 100);
            
            // Don't update the main state here since setTimeout will handle it
            return;
          }
          
          // If no assessment results, just use all students with null scores
          updatedScores[classId] = mergedStudents.map((student) => ({
            ...student,
            assessmentScores: student.assessmentScores || { ...emptyAssessmentScores }
          }));
        }
      });
      
      // Update scores for classes without assessment results
      if (Object.keys(updatedScores).length > 0) {
        setStudentsReadingScores(prev => ({
          ...prev,
          ...updatedScores
        }));
      }
    };
    
    updateReadingScores();
  }, [assessmentResultsByStudent, studentsData, selectedYearOption]);

  // When a class is selected, fetch its students
  useEffect(() => {
    if (selectedClass && selectedClass.id) {
      fetchStudentsForSelectedClass(selectedClass.id);
    }
  }, [selectedClass]);

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window?.innerWidth < window?.innerHeight);
    };

    window?.addEventListener("resize", handleOrientationChange);

    return () => {
      window?.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  // Add a safety timeout to prevent infinite loading
  useEffect(() => {
    let timeoutId;
    
    if (isLoading) {
      timeoutId = setTimeout(() => {
        console.log("Loading timeout triggered - forcing state update");
        setIsLoading(false);
        setShowReports(true);
        setError("Loading took too long. Some data may be incomplete.");
      }, 5000); // 5 second timeout
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isLoading]);

  // Refresh HubSpot widget
  useEffect(() => {
    window?.HubSpotConversations?.widget?.refresh();
  }, []);

  const filterClassesForReport = (classItem) => {
    let matchesSelectedYear = false;
    try {
      matchesSelectedYear =
        formatAcademicYear(getRecordAcademicYear(classItem), "storage") ===
        selectedYearOption?.storage;
    } catch (error) {
      matchesSelectedYear = false;
    }

    return !classItem.trial_class &&
      matchesSelectedYear &&
      ((selectedReportType === "jisi" && classItem.grade_level1) ||
       (selectedReportType === "sifc" && classItem.grade_level2) ||
       (selectedReportType === "grade3" && classItem.grade_level3));
  };

  return (
    <Container fluid className="vh-100">
      <HeadingBanner
        name={name}
        isLoggedIn={true}
        cloud={true}
        teacherid={teacherid}
      />
      <Row className="h-100">
        <LeftPanel />
        <Col sm={{ span: 10, offset: 2 }} className="blue-background">
          <div className="mx-4 mt-4 mb-2">
            <h1>{pageCopy.title}</h1>
            <p className="text-muted mb-0">{pageCopy.subheading}</p>
          </div>
          {isLoading ? (
            <div className="loading-container">
              <div className="spinner-container">
                <div className="big-ball-spinner"></div>
                <p>Loading data, please wait...</p>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="alert alert-warning" role="alert">
                  {error}
                </div>
              )}
              
              {showReports && !selectedReportType && (
                <Card className="dynamic-box mt-4">
                  <h5 className="step-indicator">Select Grade</h5>
                  <Row className="justify-content-center mb-3">
                    <Col className="col-4">
                      <div
                        className="assessment-card"
                        onClick={jisiReports}
                      >
                        <div className="card-image">
                          <img
                            src={`/assets/imgs/jisiImg.svg`}
                            className="season-assessment-icon"
                            alt={`${gradeNames.grade1} Observations`}
                          />
                        </div>
                        <div className="card-content">
                          <span>{gradeNames.grade1} Observations</span>
                        </div>
                      </div>
                    </Col>

                    <Col className="col-4">
                      <div
                        className="assessment-card"
                        onClick={sifcReports}
                      >
                        <div className="card-image">
                          <img
                            src={`/assets/imgs/jisiImg.svg`}
                            className="season-assessment-icon"
                            alt={`${gradeNames.grade2} Observations`}
                          />
                        </div>
                        <div className="card-content">
                          <span>{gradeNames.grade2} Observations</span>
                        </div>
                      </div>
                    </Col>
                    <Col className="col-4">
                      <div className="assessment-card" onClick={grade3Reports}>
                        <div className="card-image">
                          <img
                            src={`/assets/imgs/jisiImg.svg`}
                            className="season-assessment-icon"
                            alt={`${gradeNames.grade3} Observations`}
                          />
                        </div>
                        <div className="card-content">
                          <span>{gradeNames.grade3} Observations</span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              )}
              
              {/* Display accordions when a report type is selected */}
              {selectedReportType && classes.length > 0 && (
                <div className="mt-4">
                  <div className="section-header d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0 fw-bold">
                      {selectedReportType === "jisi"
                        ? `${gradeNames.grade1} Classroom Observations`
                        : selectedReportType === "sifc"
                          ? `${gradeNames.grade2} Classroom Observations`
                          : `${gradeNames.grade3} Classroom Observations`}
                    </h4>
                    <button
                      className="blue-action-button px-4 py-2"
                      onClick={() => setSelectedReportType(null)}
                    >
                      Back to Report Selection
                    </button>
                  </div>
                  
                  <div className="accordion-container">
                    {classes
                      .filter(filterClassesForReport)
                      .map((classItem, index) => (
                        <div className="accordion-item mb-3 shadow-sm" key={classItem.id}>
                          <div 
                            className="accordion-header p-3 bg-light rounded d-flex justify-content-between align-items-center"
                            onClick={() => {
                              // Toggle the selected class
                              setSelectedClass(selectedClass?.id === classItem.id ? null : classItem);
                            }}
                          >
                            <div>
                              <h6 className="mb-0 fw-bold">{classItem.name || `Class ${index + 1}`} - {classItem.grade_level}</h6>
                            </div>
                            <span className="accordion-icon">
                              {selectedClass?.id === classItem.id ? '▲' : '▼'}
                            </span>
                          </div>
                          
                          {selectedClass?.id === classItem.id && (
                            <div className="accordion-body p-3 border rounded-bottom">
                              {/* Add student data loading indicator */}
                              {studentsData[classItem.id] === null ? (
                                <div className="text-center p-4">
                                  <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  <p className="mt-2">Loading student data...</p>
                                </div>
                              ) : (
                                <>
                                  {/* Student data status */}
                                  <div className="alert alert-info mb-4">
                                    {studentsData[classItem.id]?.length > 0 ? (
                                      <p className="mb-0">Found {studentsData[classItem.id].length} students for this class</p>
                                    ) : (
                                      <p className="mb-0">No students found for this class. You may need to add students first.</p>
                                    )}
                                  </div>
                                  
                                  {/* Assessment data loading indicator */}
                                  {assessmentDataLoading[classItem.id] && (
                                    <div className="alert alert-warning mb-4">
                                      <div className="d-flex align-items-center">
                                        <div className="spinner-border spinner-border-sm text-warning me-2" role="status">
                                          <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <div className="flex-grow-1">
                                          <span>Loading assessment data...</span>
                                          {batchProgress[classItem.id] && (
                                            <div className="mt-2">
                                              <small className="text-muted">
                                                Processing in batches to avoid URL length limits
                                              </small>
                                              <div className="progress mt-1" style={{height: '4px'}}>
                                                <div 
                                                  className="progress-bar bg-warning" 
                                                  role="progressbar" 
                                                  style={{
                                                    width: `${(batchProgress[classItem.id].current / batchProgress[classItem.id].total) * 100}%`
                                                  }}
                                                  aria-valuenow={batchProgress[classItem.id].current}
                                                  aria-valuemin="0" 
                                                  aria-valuemax={batchProgress[classItem.id].total}
                                                >
                                                </div>
                                              </div>
                                              <small className="text-muted">
                                                Batch {batchProgress[classItem.id].current} of {batchProgress[classItem.id].total}
                                              </small>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Overall Standard Score vs Testcalculation indicator */}
                                  {readingScoresCalculating[classItem.id] && (
                                    <div className="alert alert-info mb-4">
                                      <div className="d-flex align-items-center">
                                        <div className="spinner-border spinner-border-sm text-info me-2" role="status">
                                          <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span>Calculating Overall Standard Score vs Test...</span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Only show report component if students exist and data is ready */}
                                  {studentsData[classItem.id]?.length > 0 && 
                                   !assessmentDataLoading[classItem.id] && 
                                   !readingScoresCalculating[classItem.id] ? (
                                    selectedReportType === "jisi" ? (
                                      <div className="jisi-report-container">
                                        <JiSiReportsOverview
                                          classDetails={classItem}
                                          classid={classItem.id}
                                          classAssessments={allClassesAssessments.filter(a => a.classid === classItem.id)}
                                          gradeLevel={classItem.grade_level}
                                          teachers={teachers}
                                          teacherid={teacherid}
                                          assessment={classItem.assessment || {}}
                                          students={studentsData[classItem.id] || []}
                                          studentsReadingScore={studentsReadingScores[classItem.id] || []}
                                          schoolid={schoolid}
                                          additionalRoles={additionalRoles}
                                          grade1Name={gradeNames.grade1}
                                          grade2Name={gradeNames.grade2}
                                        />
                                      </div>
                                    ) : selectedReportType === "sifc" ? (
                                      <div className="sifc-report-container">
                                        <SiFcReportsOverview
                                          classDetails={classItem}
                                          classid={classItem.id}
                                          classAssessments={allClassesAssessments.filter(a => a.classid === classItem.id)}
                                          gradeLevel={classItem.grade_level}
                                          teachers={teachers}
                                          teacherid={teacherid}
                                          assessment={classItem.assessment || {}}
                                          students={studentsData[classItem.id] || []}
                                          studentsReadingScore={studentsReadingScores[classItem.id] || []}
                                          schoolid={schoolid}
                                          additionalRoles={additionalRoles}
                                          grade2Name={gradeNames.grade2}
                                          grade3Name={gradeNames.grade3}
                                        />
                                      </div>
                                    ) : (
                                      <div className="classroom-observations-container">
                                        <ClassroomObservationsOverview
                                          classDetails={classItem}
                                          teachers={teachers}
                                          teacherid={teacherid}
                                          students={studentsData[classItem.id] || []}
                                          studentsReadingScore={studentsReadingScores[classItem.id] || []}
                                          additionalRoles={additionalRoles}
                                          academicYear={selectedYearOption.storage}
                                          gradeName={gradeNames.grade3}
                                        />
                                      </div>
                                    )
                                  ) : null}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                  
                  {/* Show message if no matching classes are found after filtering */}
                  {classes.filter(filterClassesForReport).length === 0 && (
                    <div className="alert alert-info mt-4" role="alert">
                      No classes found with the appropriate grade level for this report type within the selected academic year.
                    </div>
                  )}
                </div>
              )}
              
              {selectedReportType && classes.length === 0 && (
                <div className="alert alert-info mt-4" role="alert">
                  No classes found for the selected year and grade level.
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
      {isPortrait && <OrientationPrompt />}
    </Container>
  );
}

export default HandoverDocs;
